import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@/hooks/useToast';
import { restUrl } from '@/services/utils';

import {
    existingSelectionId,
    resolveSelectionToSave,
} from '../lib/publicationValues';
import type {
    ItemSelection,
    PublicationDestination,
    PublicationMode,
    PublicationValuesPayload,
} from '../types';
import { PUBLICATION_MODE } from '../types';
import { getPublicationAngularContext } from './publicationAngularBridge';

type PublishItemsVariables = {
    mode: PublicationMode;
    itemSelection: ItemSelection;
    destinations: PublicationDestination[];
    valuesPayload: PublicationValuesPayload;
};

const createSelection = async (itemSelection: ItemSelection): Promise<string> => {
    const response = await axios.post<{ selectionId: string }>(
        restUrl('selections'),
        resolveSelectionToSave(itemSelection),
    );
    return response.data.selectionId;
};

const usePublishItems = () => {
    const { toast } = useToast();

    return useMutation({
        mutationFn: async ({
            mode,
            itemSelection,
            destinations,
            valuesPayload,
        }: PublishItemsVariables) => {
            let selectionId: string;
            try {
                selectionId = existingSelectionId(itemSelection)
                    ?? await createSelection(itemSelection);
            } catch (error) {
                throw Object.assign(error instanceof Error ? error : new Error('selection failed'), {
                    publicationPhase: 'selection',
                });
            }
            const url = mode === PUBLICATION_MODE.DEPUBLISH
                ? restUrl('publications/delete')
                : restUrl('publications');
            await axios.post(url, {
                destinations,
                publicationValues: valuesPayload.publicationValues,
                publicationValuesPerDestinationChannel: valuesPayload.publicationValuesPerDestinationChannel,
                createdByFrontend: true,
                selectionId,
            });
        },
        onSuccess: (_data, variables) => {
            toast.success(
                variables.mode === PUBLICATION_MODE.DEPUBLISH
                    ? 'DEPUBLICATION_STARTED'
                    : 'PUBLICATION_STARTED',
            );
        },
        onError: (error, variables) => {
            const phase = (error as { publicationPhase?: string }).publicationPhase;
            if (phase !== 'selection' && variables.mode === PUBLICATION_MODE.DEPUBLISH) {
                toast.error('DEPUBLICATION_FAILED');
                return;
            }
            toast.error(getPublicationAngularContext().getErrorMessage(error, 'PUBLICATION_FAILED'));
        },
    });
};

export { createSelection, usePublishItems };
export type { PublishItemsVariables };
